import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';

import { environment } from '@environments/environment';

import { StorageService } from '../../../shared/services/storage.service';
import { StorageKey } from '@shared/enums/storage-keys.enum';
import {
  Probate,
  ProbateCreate,
  ProbateCreateResponse,
} from '@app/core/models/probates.model';

import { CourtService } from '../../../shared/services/court.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { EventListener } from '@app/core/observer/event.listener';
import { EventManager, ObserverEvents } from '@app/core/observer';
import { PaginatedResponse } from '@app/core/models/paginated-response.model';
import { CaseContextService } from '@app/core/services/case-context/case-context.service';
import { ApplicationType } from '@shared/enums/application-type.enum';

@Injectable({ providedIn: 'root' })
export class ProbateService implements EventListener {
  private _http = inject(HttpClient);
  private _storageService = inject(StorageService);
  private _courtService = inject(CourtService);
  private _authService = inject(AuthService);
  private _eventManager = inject(EventManager);
  private _caseContext = inject(CaseContextService);

  constructor() {
    this._eventManager.subscribe(ObserverEvents.LOGOUT, this);
  }

  probates = signal<Probate[]>([]);
  probate = signal<Probate | null>(
    this._storageService.read(StorageKey.probate),
  );

  probateId = computed(() => {
    return this.probate()?.id ? String(this.probate()?.id) : null;
  });

  hasMultipleProbates = signal(false);

  private _targetProbateId = signal<number | null>(
    this._storageService.read(StorageKey.targetProbateId),
  );
  public readonly targetProbateId = this._targetProbateId.asReadonly();

  loading = signal(true);

  cleanServiceState() {
    this.probates.set([]);
    this.probate.set(null);
    this.clearTargetProbateId();
    this._caseContext.clear();
  }

  update(eventType: ObserverEvents) {
    if (eventType === ObserverEvents.LOGOUT) {
      this.cleanServiceState();
    }
  }

  setTargetProbateId(id: number) {
    this._targetProbateId.set(id);
    this._storageService.save(StorageKey.targetProbateId, id);
  }

  clearTargetProbateId() {
    this._targetProbateId.set(null);
    this._storageService.remove(StorageKey.targetProbateId);
  }

  createProbate(data: ProbateCreate): Observable<ProbateCreateResponse> {
    return this._http
      .post<ProbateCreateResponse>(`${environment.api}/probates/`, data)
      .pipe(map((probate) => probate));
  }

  getProbates(params?: {
    court?: number;
    probate_id?: number; // This is used to set the default probate when the user is navigating to the probate dashboard from the admin page
    limit?: number;
    offset?: number;
  }): Observable<PaginatedResponse<Probate>> {
    this.loading.set(true);

    return this._http
      .get<PaginatedResponse<Probate>>(`${environment.api}/probates/`, {
        params: {
          court: params?.court ?? 0,
          limit: params?.limit ?? 10,
          offset: params?.offset ?? 0,
          probate_id: params?.probate_id ?? 0,
        },
      })
      .pipe(
        map((probates) => {
          this.probates.set(probates.results);
          //this._setDefaultProbate(probates.results, params?.probate_id);
          this.loading.set(false);
          return probates;
        }),
        catchError((error) => {
          console.error('Error getting probates', error);
          this.loading.set(false);
          return of({ count: 0, next: null, previous: null, results: [] });
        }),
      );
  }

  getProbateDetail(
    court: number,
    probate_id: number,
  ): Observable<Probate | null> {
    const params = {
      court,
      probate_id,
    };

    return this._http
      .get<Probate>(`${environment.api}/probates/${probate_id}/`, { params })
      .pipe(
        tap((probate) => {
          this._persistProbate(probate);
          return probate;
        }),
        catchError((error) => {
          console.error('Error getting probate detail', error);
          return of(null);
        }),
      );
  }

  private _persistProbate(probate: Probate) {
    this.probate.set(probate);
    this._storageService.save(StorageKey.probate, probate);
    this._authService.setSessionUserRoles(probate.roles);
    this._caseContext.setContext({
      id: probate.id,
      // For now, created applications are estate/probate; we can expand this
      // mapping when additional application types are fully supported.
      type: ApplicationType.ESTATE,
      courtId: Number(this._courtService.courtId()) || null,
      // subscriberId will be set by SubscribersStateService.setSelectedSubscriberId()
      roles: probate.roles,
      uuid: probate.uuid,
    });
  }

  userHasMultipleProbates(): Observable<boolean> {
    return this.getProbates({
      court: Number(this._courtService.courtId()),
    }).pipe(
      map((probates) => {
        this.hasMultipleProbates.set(probates.count > 1);
        return probates.count > 1;
      }),
      catchError(() => of(false)),
    );
  }
}
